import IMask from 'imask';
import intlTelInput from 'intl-tel-input';
import { ru } from 'intl-tel-input/i18n';

const TelMask = {
	ru: '(000) 000-00-00',
	kz: '000 000 0000',
	by: '00 000-00-00',
	kg: '000 000 000',
	am: '00 000000',
};

const getInputMask = (input) => {
	const country = input.getAttribute('data-country');

	return TelMask[country] || TelMask.ru;
};
const initInputTelMask = (input) => {
	const inputMask = getInputMask(input);

	input.mask = IMask(input, { mask: inputMask });
	input.setAttribute('data-placeholder', inputMask);
};
const updateInputTelMask = (input) => {
	const inputMask = getInputMask(input);

	if (input.mask) {
		input.mask.destroy();
	}

	input.mask = IMask(input, { mask: inputMask });
	input.setAttribute('data-placeholder', inputMask);
};

class Input {
	constructor(container) {
		this.init(container);
	}

	init(container) {
		this.container = container;
		this.input = this.container.querySelector('input, textarea');

		if (!this.input || this.container.inited) {
			return;
		}

		this.container.inited = true;

		this.container.customInput = this;
		this.input.customInput = this;
		this.type = this.container.getAttribute('data-input');
		this.hints = this.container.querySelector('.input__hints');

		this.container.classList.toggle('_has-value', this.input.value.length);
		this.input.addEventListener('change', () => {
			let cleanValue = this.input.value.trim();

			if (this.type === 'tel' || this.type === 'number') {
				cleanValue = cleanValue.replace(/\D/g, '');
			}

			this.input.value = cleanValue;

			if (this.input.hasAttribute('maxlength')) {
				const maxLength = this.input.getAttribute('maxlength');

				this.input.value.length > maxLength && (this.input.value = this.input.value.substring(0, maxLength));
			}

			this.container.classList.toggle('_has-value', cleanValue.length);
		});
		this.input.addEventListener('input', () => {
			let cleanValue = this.input.value.replace(/^\s+/, '');

			if (this.type === 'tel' || this.type === 'number') {
				cleanValue = cleanValue.replace(/\D/g, '');
			}

			this.input.value = cleanValue;

			if (this.input.hasAttribute('maxlength')) {
				const maxLength = this.input.getAttribute('maxlength');

				this.input.value.length > maxLength && (this.input.value = this.input.value.substring(0, maxLength));
			}

			this.container.classList.toggle('_has-value', cleanValue.length);
		});
		this.input.addEventListener('blur', () => {
			if (this.input.touched) {
				return;
			}

			this.input.touched = true;
			this.input.setAttribute('data-touched', this.input.touched);
			this.container.classList.toggle('_touched', this.input.touched);
		});

		if (this.type === 'tel') {
			this._initTelInput(this.input);
		}
		if (this.type === 'birthdate') {
			IMask(this.input, {
				mask: Date,
				min: new Date(1900, 1, 1),
				max: new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
			});
		}

		if (this.hints) {
			const onWindowClick = (evt) => {
				if (!this.container.contains(evt.target)) {
					toggleHints(false);
				}
			};

			const toggleHints = (isVisible) => {
				this.hints.hidden = !isVisible;

				const eventName = isVisible ? 'hints:open' : 'hints:close';
				this.input.dispatchEvent(
					new CustomEvent(eventName, {
						detail: { hints: this.hints },
					}),
				);

				isVisible
					? window.addEventListener('click', onWindowClick)
					: window.removeEventListener('click', onWindowClick);
			};

			this.input.addEventListener('input', () => {
				toggleHints(!!this.input.value);
			});

			this.input.addEventListener('focus', () => {
				toggleHints(!!this.input.value);
			});

			Array.from(this.hints.children).forEach((hint) => {
				hint.addEventListener('click', () => {
					const value = hint.getAttribute('data-input-hint-value');
					this.input.setAttribute('data-hint-value', value);
					this.input.value = value;
					this.input.dispatchEvent(new Event('change'));
					toggleHints(false);
				});
			});
		}

		setTimeout(() => {
			this.container.querySelector('.input__clear-button')?.addEventListener('click', () => {
				this.setValue('');
				this.input.focus();
			});
		}, 1000);
	}
	setValue(value) {
		this.input.value = value;
		this.input.dispatchEvent(new Event('input'));
		this.input.dispatchEvent(new Event('change'));
	}
	_initTelInput(input) {
		if (input.iti) {
			return;
		}

		let triggeredByTyping = false;

		const iti = intlTelInput(input, {
			countryOrder: ['ru', 'kz', 'kg', 'am', 'by'],
			i18n: ru,
			initialCountry: 'ru',
			autoPlaceholder: 'off',
			onlyCountries: ['ru', 'am', 'by', 'kg', 'kz'],
			loadUtils: () => import('intl-tel-input/utils'),
			countrySearch: false,
			separateDialCode: true,
			useFullscreenPopup: false,
		});
		input.iti = iti;

		input.addEventListener('keydown', () => {
			triggeredByTyping = true;
		});

		iti.countryList.addEventListener('mousedown', () => {
			triggeredByTyping = false;
		});

		input.iti.promise.then(() => {
			input.setAttribute('data-country', iti.getSelectedCountryData().iso2);
			input.setAttribute('data-country-code', `+${iti.getSelectedCountryData().dialCode}`);

			initInputTelMask(input);

			input.addEventListener('countrychange', () => {
				if (triggeredByTyping && iti.getSelectedCountryData().iso2 === 'ru') {
					iti.setCountry('ru');
				}
				if (triggeredByTyping && iti.getSelectedCountryData().iso2 === 'kz') {
					iti.setCountry('kz');
				}

				triggeredByTyping = false;

				input.setAttribute('data-country', iti.getSelectedCountryData().iso2);
				input.setAttribute('data-country-code', `+${iti.getSelectedCountryData().dialCode}`);

				if (input.mask) {
					updateInputTelMask(input);
				}

				input.focus();
			});

			const paddingPx = parseFloat(getComputedStyle(input).paddingLeft);
			const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
			const paddingRem = paddingPx / rootFontSize;
			input.style.paddingLeft = `${paddingRem.toFixed(3)}rem`;
		});
	}
}

document.querySelectorAll('[data-input]').forEach((input) => new Input(input));

export default Input;
